import { presetToRange, previousRange, type DateRange } from '../../../lib/reportingDateRange';

// Organic Social Reporting — period selector (pure).
//
// Presets: This Month · Last Month · Past 7 Days · Past 30 Days · Custom, each vs
// a "previous comparable period". The comparison is preset-AWARE, not a blind
// equal-length shift, so month presets compare fairly:
//   This Month → Last Month, SAME elapsed-day span (month-to-date vs month-to-date)
//   Last Month → the month before, full calendar month
//   Past 7 / Past 30 → the immediately-prior 7 / 30 days
//   Custom → the immediately-preceding equal-length window
//
// ⚠️ This is a SOCIAL-ONLY helper. reportingDateRange.ts (presetToRange /
// ReportingDatePreset) is SHARED with the Meta ads reporting page + its
// DateRangePicker + accountAnalytics; we reuse its rolling-window + this_month
// presets but NEVER change them. `last_month` has no ads-preset equivalent, so its
// month math lives here.

export type SocialPeriodPreset = 'this_month' | 'last_month' | 'past_7' | 'past_30' | 'custom';

export const SOCIAL_PERIOD_OPTIONS: Array<{ value: SocialPeriodPreset; label: string }> = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'past_7', label: 'Past 7 Days' },
  { value: 'past_30', label: 'Past 30 Days' },
  { value: 'custom', label: 'Custom' },
];

export const DEFAULT_SOCIAL_PERIOD: SocialPeriodPreset = 'past_30';

// Local-calendar YMD (matches presetToRange's convention — "This Month" means the
// browser's month).
function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Whole previous calendar month [1st .. last day].
function lastMonthRange(now: Date): DateRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  return { from: toYmd(new Date(y, m - 1, 1)), to: toYmd(new Date(y, m, 0)) };
}

// Whole month BEFORE last [1st .. last day] — Last Month's comparison window.
function monthBeforeLastRange(now: Date): DateRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  return { from: toYmd(new Date(y, m - 2, 1)), to: toYmd(new Date(y, m - 1, 0)) };
}

// Last month's month-to-date, clamped to the same day-of-month as today — the fair
// comparison for This Month (e.g. 15 elapsed days vs last month's first 15 days).
// Clamps when last month is shorter (Mar 31 → Feb 28).
function thisMonthPreviousRange(now: Date): DateRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  const daysInLastMonth = new Date(y, m, 0).getDate();
  const toDay = Math.min(now.getDate(), daysInLastMonth);
  return { from: toYmd(new Date(y, m - 1, 1)), to: toYmd(new Date(y, m - 1, toDay)) };
}

/** Resolve a preset to its concrete range. `custom` required for 'custom'. */
export function socialPeriodRange(
  preset: SocialPeriodPreset,
  now: Date,
  custom?: DateRange,
): DateRange {
  switch (preset) {
    case 'this_month':
      return presetToRange('this_month', now); // {1st of month .. today}
    case 'last_month':
      return lastMonthRange(now);
    case 'past_7':
      return presetToRange('past_7', now); // {today-7 .. today-1}
    case 'past_30':
      return presetToRange('past_30', now); // {today-30 .. today-1}
    case 'custom':
      if (!custom) throw new Error('custom period requires an explicit range');
      return custom;
  }
}

/** The comparable previous window for a preset (see the module note). */
export function socialPreviousRange(
  preset: SocialPeriodPreset,
  now: Date,
  custom?: DateRange,
): DateRange {
  switch (preset) {
    case 'this_month':
      return thisMonthPreviousRange(now);
    case 'last_month':
      return monthBeforeLastRange(now);
    case 'past_7':
    case 'past_30':
      return previousRange(socialPeriodRange(preset, now));
    case 'custom':
      if (!custom) throw new Error('custom period requires an explicit range');
      return previousRange(custom);
  }
}

/** Range + its comparable previous, both from one `now` (kept consistent). */
export function socialPeriodRanges(
  preset: SocialPeriodPreset,
  now: Date,
  custom?: DateRange,
): { range: DateRange; previous: DateRange } {
  return {
    range: socialPeriodRange(preset, now, custom),
    previous: socialPreviousRange(preset, now, custom),
  };
}

/** A human label for the compared window (e.g. "vs 1 Jun – 30 Jun"). */
export function comparisonLabel(prev: DateRange): string {
  return `vs ${formatShortDate(prev.from)} – ${formatShortDate(prev.to)}`;
}

function formatShortDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}
